<template>
  <v-flex>
    <v-card-title v-if="search">
      <slot name="left-filters" />
      <v-spacer />
      <v-text-field
        v-model="searchText"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        clearable
        :hide-details="!searchHint"
        :hint="searchHint"
        :persistent-hint="!!searchHint"
      />
    </v-card-title>
    <slot name="search-filters" />
    <v-data-table
      ref="vDataTable"
      class="ftItemSelectList"
      :mobile-breakpoint="0"
      :headers="filteredHeaders"
      :items="items"
      :hide-default-footer="hidePagination"
      :server-items-length="totalItems || undefined"
      :search="searchText"
      :loading="loading"
      :options.sync="localPagination"
      :custom-filter="filter"
      :footer-props="localPagination ? {itemsPerPageOptions: rowsPerPageItems} : undefined"
      :expanded.sync="expanded"
      :items-per-page="localPagination ? undefined : -1"
      @click:row="rowClicked"
    >
      <template v-slot:item="props">
        <tr
          :class="getRowClass(props.item, props.index).concat([{'v-data-table__expanded': props.isExpanded, 'v-data-table__expanded__row': props.isExpanded}])"
          @click="doEdit(props.item.id, props.index)"
        >
          <td
            v-for="(header, i) of filteredHeaders"
            :key="i"
            :class="getCellClass(header, props.item, i, props.index)"
            @click="toggleExpand($event, props, i)"
          >
            <template v-if="expand && !i">
              <v-btn
                :color="getExpandColor(props)"
                text
                icon
                @click="toggleExpand($event, props, i)"
              >
                <v-icon
                  :class="{'v-data-table__expand-icon': true, 'v-data-table__expand-icon--active': props.isExpanded && transitioned[props.item.id]}"
                >
                  {{ getExpandIcon(props) }}
                </v-icon>
              </v-btn>
            </template>
            <template v-else-if="useCellHtml(header, props.item, i)">
              <div v-html="getCellHtml(header, props.item, i)" />
            </template>
            <template v-else>
              <v-icon
                v-if="getCellIcon(header, props.item, i)"
                :class="getCellIconClass(header, props.item, i)"
              >
                {{ getCellIcon(header, props.item, i) }}
              </v-icon>
              {{ getCellText(header, props.item, i) }}
              <slot
                :name="header.value"
                :row="props.index"
                :column="i"
                :item="props.item"
                :header="header"
                :items="items"
                :props="props"
                :value="props.item[header.value]"
              />
            </template>
          </td>
        </tr>
      </template>
      <template
        v-if="expand"
        v-slot:expanded-item="props"
      >
        <tr
          :key="props.item.id"
          :class="{'v-data-table__expanded v-data-table__expanded__content': true, 'ft-expand-closing': !transitioned[props.item.id]}"
        >
          <td
            :colspan="props.headers.length"
            class="pa-0 ma-0"
            style="height: auto;"
          >
            <v-expand-transition>
              <div v-show="transitioned[props.item.id]">
                <slot
                  name="expand-content"
                  :item="props.item"
                  :props="props"
                />
              </div>
            </v-expand-transition>
          </td>
        </tr>
      </template>
    </v-data-table>
    <item-editor-buttons
      v-if="!readOnly && addAction"
      :save-action="addAction"
      save-text="Add"
    />
  </v-flex>
</template>

<script>
import tableCommon from '../mixins/tableCommon'
import {rowsPerPageItems} from '../util/values'
import ItemEditorButtons from './ItemEditorButtons'

const defaultIcon = 'mdi-chevron-down'

export default {
  name: 'ItemSelectTable',

  components: {ItemEditorButtons},

  mixins: [tableCommon],

  props: {
    items: Array,
    headers: Array,
    readOnly: Boolean,
    addAction: Function,
    addLabel: String,
    editAction: Function,
    filter: Function,
    search: Boolean,
    searchValue: String,
    expand: Boolean,
    searchHint: String,
    totalItems: Number,
    expandColor: [String, Function],
    disabledField: {
      type: String,
      default: 'disabled'
    },
    expandIcon: {
      type: [String, Function],
      default: defaultIcon
    },
    rowClass: {
      type: Function,
      default: () => () => {}
    },
    pagination: Object,
    rowsPerPageItems: {
      type: Array,
      default: () => rowsPerPageItems
    },
    loading: [Boolean, String]
  },

  data: function () {
    return {
      searchText: '',
      expanded: [],
      transitioned: [],
      closeTimeouts: {}
    }
  },

  computed: {
    localPagination: {
      get () {
        return this.pagination
      },
      set (val) {
        if (this.pagination) this.$emit('update:pagination', val)
      }
    },
    hidePagination () {
      return !this.pagination
    },
    getAddLabel () {
      return this.$fts.add.text
    },
    filteredHeaders () {
      if (this.expand) {
        return [this.expansionCell].concat(this.headers)
      }
      return this.headers
    },
    expansionCell () {
      return {sortable: false, cellClass: 'ftListExpandButton'}
    }
  },
  watch: {
    searchValue (val) {
      if (this.searchText !== val) {
        this.searchText = val
      }
    },
    searchText (val) {
      this.$emit('search', val)
    }
  },
  mounted () {
    this.searchText = this.searchValue
    this.localPagination = this.pagination
  },

  methods: {
    rowClicked (item) {
      this.doEdit(item.id, this.items.indexOf(item))
    },
    getRowClass (row, index) {
      const rowClass = {'ft-row-disabled': this.disabledField && row[this.disabledField]}
      const rowClasses = [rowClass]
      const extendedRowClass = this.rowClass(row, index)
      if (extendedRowClass) {
        if (Array.isArray(extendedRowClass)) {
          rowClasses.push(...extendedRowClass)
        } else if (typeof extendedRowClass === 'object') {
          rowClasses.push(extendedRowClass)
        } else if (typeof extendedRowClass === 'string') {
          rowClass[extendedRowClass] = true
        }
      }
      if (index % 2) rowClasses.push('ft-even')
      return rowClasses
    },
    toggleExpand (e, props, cell) {
      if (this.expand && !cell) {
        const item = props.item
        const id = item.id
        e.stopPropagation()
        if (props.isExpanded && this.transitioned[id]) {
          this.closeExpand(item)
        } else {
          clearTimeout(this.closeTimeouts[id])
          // Signal to build expanded content
          props.expand(true)
          // Show expansion content with transition animation after it's had time to get added to the DOM
          this.$nextTick(() => this.$set(this.transitioned, id, true))
          // Hide others (in a timeout so the close animation starts around the same time as the expand animation)
          this.$nextTick(() => this.expanded.forEach(i => i !== item && this.closeExpand(i)))
        }

      }
    },
    closeExpand (item) {
      const id = item.id
      this.$set(this.transitioned, id, false)
      // Remove expansion content from DOM after transition animation has had enough time to finish
      this.closeTimeouts[id] = setTimeout(() => this.$refs.vDataTable.expand(item, false), 600)
    },
    getExpandColor (props) {
      if (typeof this.expandColor === 'function') {
        return this.expandColor(props)
      }
      return this.expandColor
    },
    getExpandIcon (props) {
      if (typeof this.expandIcon === 'function') {
        return this.expandIcon(props) || defaultIcon
      }
      return this.expandIcon || defaultIcon
    },
    doAdd () {
      if (this.addAction) {
        this.addAction(null)
      }
    },
    doEdit (id, index) {
      console.log('doEdit', id, index)
      if (this.editAction) {
        this.editAction(id, index)
      }
    }
  }
}
</script>
<style>
  .ftItemSelectList.v-data-table table tr.ft-even {
    background-color:rgba(0, 0, 0, 0.03);
  }
  /* .ftItemSelectList.v-data-table table thead tr th, */
  .ftItemSelectList.v-data-table table tbody tr td {
    padding: 0 10px;
  }
  .ftItemSelectList.v-data-table tbody td {
    font-size: 13px;
  }
  .ftListDateTime, .ftListDate, .ftListTime {
    white-space: nowrap;
  }
  .ftItemSelectList th {
    white-space: normal !important;
  }
  .ftListDateTime {
    display: block;
  }
  .ftItemSelectList.v-data-table table td.ftListExpandButton,
  .ftItemSelectList.v-data-table table td.ft-thin-cell {
    width: 0.1%;
    overflow: hidden;
  }
  /*
  .ftItemSelectList.v-data-table table td.ftListExpandButton {
    padding-right: 10px;
    margin-right: 0;
  }
  */
  .ft-row-disabled {
    text-decoration: line-through
  }
  .ft-expand-closing td {
    border-bottom: none !important;
  }
</style>
